import { Text, View, ScrollView, TouchableOpacity, RefreshControl, useWindowDimensions } from 'react-native';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Animated, { FadeInDown } from 'react-native-reanimated';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import Avatar from '@/components/People/Avatar';
import { deleteSmallGroup } from '@/data/smallGroups';
import { closeBibleStudy, reopenBibleStudy } from '@/data/bibleStudies';
import usePlatform from '@/utils/hooks/usePlatform';
import { showAlert } from '@/utils/showAlert';
import { useNavigation, useRouter, useLocalSearchParams } from 'expo-router';
import { StyleSheet } from 'react-native-unistyles';
import PageTitle from '@/components/General/PageTitle';
import { saveToRecents } from '@/utils/recentlyVisited';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import { TabView } from 'react-native-tab-view';
import ReachTabBar from '@/components/General/ReachTabBar';
import Unicon from '@/components/General/Unicon';
import BibleStudySessionsTable from '@/components/BibleStudies/BibleStudySessionsTable';
import BibleStudyStudents from '@/components/BibleStudies/BibleStudyStudents';
import CutCard from '@/components/General/CutCard';
import ContentLoader from '@/components/General/ContentLoader';
import ActionButton from '@/components/General/ActionButton';
import Toast from 'react-native-toast-message';
import useBibleStudies from '@/utils/hooks/useBibleStudies';
import React from 'react';

// ============ HELPER ============
const getStatusColors = (status) => {
    switch (status) {
        case "ACTIVE":
            return { bg: "successContainer", text: "success" };
        case "COMPLETED":
            return { bg: "infoContainer", text: "info" };
        case "CANCELED":
            return { bg: "errorContainer", text: "error" };
        case "PAUSED":
            return { bg: "warningContainer", text: "warning" };
        default:
            return { bg: "surface", text: "onSurface" };
    }
};

const isClosedStatus = (status) => status === 'COMPLETED' || status === 'CANCELED';

// ============ INFO ROW ============
const InfoRow = ({ icon, label, value, onPress, children, isEmpty }) => {
    if (!value && !children && !isEmpty) return null;
    const content = (
        <View style={styles.infoRow}>
            <View style={styles.infoRowLeft}>
                <Unicon name={icon} size={18} colorVariant="textSecondary" />
                <Text style={styles.infoRowLabel}>{label}</Text>
            </View>
            <View style={styles.infoRowRight}>
                {children || <Text style={[styles.infoRowValue, onPress && styles.linkText, isEmpty && styles.emptyText]}>{value}</Text>}
            </View>
        </View>
    );
    return onPress ? <TouchableOpacity onPress={onPress}>{content}</TouchableOpacity> : content;
};

// ============ INFO PANEL (isolated component) ============
const BibleStudyInfoPanel = React.memo(({ smallGroupID, canAct }) => {
    const router = useRouter();
    const queryClient = useQueryClient();
    const { data: study, isFetching } = Query.smallGroups.byId({ id: smallGroupID });
    const { data: closedTeacher } = Query.people.basicById({ id: study?.closedPrimaryTeacherID, options: { enabled: !!study?.closedPrimaryTeacherID } });

    const invalidate = () => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.smallGroups.byId(smallGroupID) });
        queryClient.invalidateQueries({ queryKey: ['bibleStudies'] });
        queryClient.invalidateQueries({ queryKey: ['smallGroups'] });
    };

    const closeMutation = useMutation({
        mutationFn: closeBibleStudy,
        onSuccess: () => {
            invalidate();
            Toast.delayShow({ type: 'success', text2: 'Study closed' });
        },
    });

    const reopenMutation = useMutation({
        mutationFn: reopenBibleStudy,
        onSuccess: () => {
            invalidate();
            Toast.delayShow({ type: 'success', text2: 'Study reopened' });
        },
    });

    const confirmClose = (outcome) => {
        const label = outcome === 'COMPLETED' ? 'Complete' : 'Cancel';
        const teacherName = study?.Leader ? `${study.Leader.firstName} ${study.Leader.lastName}` : 'the primary teacher';
        showAlert({
            title: `${label} Study`,
            subtitle: `This records a ${outcome === 'COMPLETED' ? 'completion' : 'cancellation'} against ${teacherName} as the primary teacher. The study can be reopened later if needed.`,
            confirmText: `${label} Study`,
            cancelText: 'Keep Teaching',
            isDestructive: outcome === 'CANCELED',
            onConfirm: () => closeMutation.mutate({ smallGroupID: study.id, outcome }),
        });
    };

    const confirmReopen = () => {
        showAlert({
            title: 'Reopen Study',
            subtitle: 'This returns the study to Active so sessions can be reported again. The previous closure outcome is not kept.',
            confirmText: 'Reopen',
            cancelText: 'Cancel',
            onConfirm: () => reopenMutation.mutate({ smallGroupID: study.id }),
        });
    };

    const refresh = () => invalidate();
    const statusColors = getStatusColors(study?.status);
    const closed = isClosedStatus(study?.status);

    return (
        <View style={styles.leftPanel}>
            <ScrollView
                contentContainerStyle={styles.leftPanelContent}
                keyboardShouldPersistTaps="handled"
                refreshControl={<RefreshControl refreshing={isFetching} onRefresh={refresh} />}
                showsVerticalScrollIndicator={false}
            >
                {/* Header Card */}
                <Animated.View entering={FadeInDown.duration(400)}>
                <CutCard accentColor="primary" style={styles.headerCard}>
                    {/* Type Badge */}
                    <View style={styles.typeBadge}>
                        <Unicon name="book" size={14} colorVariant="groups" />
                        <Text style={styles.typeBadgeText}>Bible Study</Text>
                    </View>

                    {/* Title Row */}
                    <View style={styles.titleRow}>
                        <View style={styles.iconBadge}>
                            <Unicon name="book-outline" size={24} colorVariant="groups" />
                        </View>
                        <View style={styles.titleInfo}>
                            <Text style={styles.groupTitle}>{study?.name}</Text>
                            {study?.Leader && (
                                <TouchableOpacity onPress={() => router.push(`/people/${study.Leader.id}`)}>
                                    <Text style={styles.leaderSubtitle}>Taught by {study.Leader.firstName} {study.Leader.lastName}{study?.CoLeader ? ` & ${study.CoLeader.firstName} ${study.CoLeader.lastName}` : ''}</Text>
                                </TouchableOpacity>
                            )}
                        </View>
                    </View>

                    {/* Stats Row */}
                    <View style={styles.statsRow}>
                        <View style={styles.statItem}>
                            <Text style={styles.statLabel}>Students</Text>
                            <Text style={styles.statValue}>{study?.Students?.length || 0}</Text>
                        </View>
                        <View style={styles.statDivider} />
                        <View style={styles.statItem}>
                            <Text style={styles.statLabel}>Sessions</Text>
                            <Text style={styles.statValue}>{study?.Event?.EventInstances?.length || 0}</Text>
                        </View>
                        <View style={styles.statDivider} />
                        <View style={styles.statItem}>
                            <View style={styles.statusPill(statusColors.bg, statusColors.text)}>
                                <Text style={styles.statusPillText(statusColors.text)}>{study?.status || '-'}</Text>
                            </View>
                        </View>
                    </View>

                    {/* Primary Teacher Row */}
                    {study?.Leader && (
                        <View style={styles.leaderRow}>
                            <Avatar size={40} profile={study.Leader} disableBorder onPress={() => router.push(`/people/${study.Leader.id}`)} />
                            <View style={styles.leaderInfo}>
                                <Text style={styles.leaderLabel}>Primary Teacher</Text>
                                <Text style={styles.leaderName}>{study.Leader.firstName} {study.Leader.lastName}</Text>
                            </View>
                        </View>
                    )}

                    {/* Co-Teacher Row */}
                    {study?.CoLeader && (
                        <View style={styles.leaderRow}>
                            <Avatar size={40} profile={study.CoLeader} disableBorder onPress={() => router.push(`/people/${study.CoLeader.id}`)} />
                            <View style={styles.leaderInfo}>
                                <Text style={styles.leaderLabel}>Co-Teacher</Text>
                                <Text style={styles.leaderName}>{study.CoLeader.firstName} {study.CoLeader.lastName}</Text>
                            </View>
                        </View>
                    )}

                    {/* Action Buttons */}
                    <View style={styles.actionRow}>
                        {!closed && canAct && (
                            <ActionButton
                                iconName="add"
                                text="Log Session"
                                testID="bible-study-log-session"
                                onPress={() => router.push({ pathname: 'biblestudies/sessions/create', params: { smallGroupID: study.id } })}
                            />
                        )}
                        {canAct && (
                            <ActionButton
                                iconName="create"
                                text="Edit"
                                testID="bible-study-edit"
                                onPress={() => router.push({ pathname: 'biblestudies/create', params: { id: study.id } })}
                            />
                        )}
                        {!closed && canAct && (
                            <ActionButton
                                iconName="checkmark-done-outline"
                                text="Complete"
                                testID="bible-study-complete"
                                onPress={() => confirmClose('COMPLETED')}
                            />
                        )}
                        {closed && canAct && (
                            <ActionButton
                                iconName="refresh-outline"
                                text="Reopen"
                                testID="bible-study-reopen"
                                onPress={confirmReopen}
                            />
                        )}
                    </View>
                </CutCard>
                </Animated.View>

                {/* Info Section */}
                <Animated.View entering={FadeInDown.delay(150).duration(400)}>
                <CutCard accentColor="primary" style={styles.infoSection}>
                    <Text style={styles.sectionLabel}>Details</Text>
                    <InfoRow icon="text" label="Name" value={study?.name} />
                    {study?.lessonTime && <InfoRow icon="time" label="Session Time" value={study.lessonTime} />}
                    {study?.lessonDay && <InfoRow icon="calendar" label="Session Day" value={study.lessonDay} />}
                    {closed && (
                        <InfoRow
                            icon="flag-outline"
                            label={study?.status === 'COMPLETED' ? 'Completed' : 'Canceled'}
                            value={study?.closedAt ? new Date(study.closedAt).toLocaleDateString() : 'Unknown date'}
                        />
                    )}
                    {closed && (
                        <InfoRow
                            icon="person-outline"
                            label="Recorded under"
                            value={closedTeacher ? `${closedTeacher.firstName} ${closedTeacher.lastName}` : (study?.closedPrimaryTeacherID ? 'Former teacher' : 'Unknown teacher')}
                        />
                    )}
                </CutCard>
                </Animated.View>

                {/* Custom Fields */}
                {(() => {
                    const customFields = study?.SmallGroupType?.CustomFields || [];
                    const customFieldValues = study?.customFieldValues || {};
                    const fieldsWithValues = customFields.filter(f => {
                        const v = customFieldValues[f.id];
                        return v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0);
                    });
                    if (!fieldsWithValues.length) return null;
                    return (
                        <Animated.View entering={FadeInDown.delay(225).duration(400)}>
                        <CutCard accentColor="primary" style={styles.infoSection}>
                            <Text style={styles.sectionLabel}>Custom Fields</Text>
                            {fieldsWithValues.map(field => {
                                const raw = customFieldValues[field.id];
                                let display;
                                if (field.type === 'Checkbox') display = raw ? 'Yes' : 'No';
                                else if (Array.isArray(raw)) display = raw.join(', ');
                                else display = String(raw);
                                return <InfoRow key={field.id} icon="reader-outline" label={field.name} value={display} />;
                            })}
                        </CutCard>
                        </Animated.View>
                    );
                })()}

                {/* Students Preview */}
                {study?.Students?.length > 0 && (
                    <Animated.View entering={FadeInDown.delay(300).duration(400)}>
                    <CutCard accentColor="primary" style={styles.studentsPreview}>
                        <Text style={styles.sectionLabel}>Students ({study.Students.length})</Text>
                        <View style={styles.studentsAvatarRow}>
                            {study.Students.slice(0, 6).map((student) => (
                                <Avatar key={student.memberID} size={36} profile={student.Person} onPress={() => router.push(`/people/${student.Person?.id}`)} />
                            ))}
                            {study.Students.length > 6 && (
                                <View style={styles.moreBadge}>
                                    <Text style={styles.moreBadgeText}>+{study.Students.length - 6}</Text>
                                </View>
                            )}
                        </View>
                    </CutCard>
                    </Animated.View>
                )}
            </ScrollView>
        </View>
    );
});

// ============ STUDENTS TAB (isolated component) ============
const StudentsTab = React.memo(({ smallGroupID, canReadNotes }) => {
    return (
        <View style={styles.tabContent}>
            <BibleStudyStudents smallGroupID={smallGroupID} canReadNotes={canReadNotes} />
        </View>
    );
});

// ============ SESSIONS TAB (isolated component) ============
const SessionsTab = React.memo(({ smallGroupID, scope }) => {
    const { data: study } = Query.smallGroups.byId({ id: smallGroupID });
    return (
        <View style={styles.tabContent}>
            <BibleStudySessionsTable
                scope={scope}
                smallGroupID={smallGroupID}
                title="Sessions"
                description={`All sessions for ${study?.name || 'this study'}`}
                showSmallGroupInfo={false}
            />
        </View>
    );
});

// ============ MAIN SCREEN ============
function BibleStudyDetailsScreen() {
    const [mobileTab, setMobileTab] = useState(0);
    const [activityTab, setActivityTab] = useState(0);
    const [initialPageApplied, setInitialPageApplied] = useState(false);

    const { isNative } = usePlatform();
    const navigation = useNavigation();
    const router = useRouter();
    const { bibleStudyID, tab } = useLocalSearchParams();
    const { width } = useWindowDimensions();
    const isWideScreen = width >= 900;

    const queryClient = useQueryClient();
    const { data: loggedInUser } = Query.people.loggedInUser();
    const { data: study, isFetching: fetchingStudy } = Query.smallGroups.byId({ id: bibleStudyID });
    const { enabled, loading, canView, canManage } = useBibleStudies();

    const isAssigned = loggedInUser?.id === study?.leaderID || loggedInUser?.id === study?.coLeaderID;
    const canAct = isAssigned || canManage;
    const isStudy = study?.SmallGroupType?.purpose === 'BIBLE_STUDY';

    // Flag/permission/wrong-module guards — UI gating only.
    useEffect(() => {
        if (loading || !study?.id) return;
        if (!enabled || !canView || !isStudy) {
            // Studies live in the legacy module when the flag is off; ordinary
            // groups stay in Small Groups. Wrong door → route to the right one.
            router.replace(isStudy ? `/smallgroups/${bibleStudyID}` : '/');
        } else if (!canAct && !loggedInUser?.can?.('View Bible Studies')) {
            router.canGoBack() ? router.back() : router.replace('/');
        }
    }, [loading, enabled, canView, isStudy, canAct, study?.id, loggedInUser]);

    if (!bibleStudyID) {
        router.canGoBack() ? router.back() : router.replace("/");
    }

    // Track recently visited
    useEffect(() => {
        if (study?.id && enabled && isStudy) {
            saveToRecents({
                id: study.id,
                url: `/biblestudies/${study.id}`,
                title: study.name,
                params: { bibleStudyID: study.id },
            });
        }
    }, [study?.id, enabled, isStudy]);

    const deleteStudyMutation = useMutation({
        mutationFn: deleteSmallGroup,
        onSuccess: () => {
            router.back();
            queryClient.invalidateQueries({ queryKey: ['bibleStudies'] });
            queryClient.invalidateQueries({ queryKey: ['smallGroups'] });
            queryClient.invalidateQueries({ queryKey: ['dashboardData'] });
            queryClient.invalidateQueries({ queryKey: ['people'] });
        }
    });

    const closeMutation = useMutation({
        mutationFn: closeBibleStudy,
        onSuccess: (_d, vars) => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.smallGroups.byId(bibleStudyID) });
            queryClient.invalidateQueries({ queryKey: ['bibleStudies'] });
            Toast.delayShow({ type: 'success', text2: vars?.outcome === 'COMPLETED' ? 'Study marked complete' : 'Study canceled' });
        },
    });

    const reopenMutation = useMutation({
        mutationFn: reopenBibleStudy,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.smallGroups.byId(bibleStudyID) });
            queryClient.invalidateQueries({ queryKey: ['bibleStudies'] });
            Toast.delayShow({ type: 'success', text2: 'Study reopened' });
        },
    });

    useEffect(() => {
        const options = [];
        const closed = isClosedStatus(study?.status);
        const teacherName = study?.Leader ? `${study.Leader.firstName} ${study.Leader.lastName}` : 'the primary teacher';

        if (study?.id && canAct && !closed) {
            options.push({
                name: "Log a Session",
                icon: dropdownIcons.add,
                onSelect: () => router.push({ pathname: 'biblestudies/sessions/create', params: { smallGroupID: study.id } })
            });
            options.push({
                name: "Edit Study",
                icon: dropdownIcons.edit,
                onSelect: () => router.push({ pathname: 'biblestudies/create', params: { id: study.id } })
            });
            options.push({
                name: "Complete Study",
                icon: { web: 'checkmark-done-outline' },
                onSelect: () => showAlert({
                    title: "Complete Study",
                    subtitle: `This records a completion against ${teacherName} as the primary teacher. The study can be reopened later if needed.`,
                    confirmText: "Complete Study",
                    cancelText: "Keep Teaching",
                    onConfirm: () => closeMutation.mutate({ smallGroupID: study.id, outcome: 'COMPLETED' })
                })
            });
            options.push({
                name: "Cancel Study",
                icon: { web: 'close-outline' },
                destructive: true,
                onSelect: () => showAlert({
                    title: "Cancel Study",
                    subtitle: `This records a cancellation against ${teacherName} as the primary teacher. The study can be reopened later if needed.`,
                    isDestructive: true,
                    confirmText: "Cancel Study",
                    cancelText: "Keep Teaching",
                    onConfirm: () => closeMutation.mutate({ smallGroupID: study.id, outcome: 'CANCELED' })
                })
            });
        }

        if (study?.id && canAct && closed) {
            options.push({
                name: "Reopen Study",
                icon: { web: 'refresh-outline' },
                onSelect: () => showAlert({
                    title: "Reopen Study",
                    subtitle: "This returns the study to Active so sessions can be reported again. The previous closure outcome is not kept.",
                    confirmText: "Reopen",
                    cancelText: "Cancel",
                    onConfirm: () => reopenMutation.mutate({ smallGroupID: study.id })
                })
            });
        }

        if (study?.id && canManage) {
            options.push({
                name: "Delete Study",
                icon: dropdownIcons.delete,
                destructive: true,
                onSelect: () => showAlert({
                    title: "Delete Bible Study",
                    subtitle: "Are you sure you want to delete this Bible Study? This will also delete all sessions and you will not be able to recover them.",
                    isDestructive: true,
                    confirmText: "Delete",
                    onConfirm: () => {
                        if (!study?.id) return;
                        deleteStudyMutation.mutate(study.id);
                    }
                })
            });
        }

        navigation.setOptions({
            headerTitle: () => <PageTitle title={study?.name} subtitle="Bible Study" />,
            headerRight: options.length == 0 ? null : () => <DropdownMenu testID="HeaderMenu" options={options} />,
        });
    }, [navigation, study?.name, study?.status, study?.leaderID, study?.coLeaderID, study?.id, loggedInUser, canAct, canManage]);

    // ============ TAB ROUTES ============
    const mobileRoutes = useMemo(() => [
        { key: 'info', title: 'Info' },
        { key: 'sessions', title: 'Sessions' },
        { key: 'students', title: 'Students' }
    ], []);

    const activityRoutes = useMemo(() => [
        { key: 'sessions', title: 'Sessions' },
        { key: 'students', title: 'Students' }
    ], []);

    // Apply tab param (or auto-jump to Sessions if user is the teacher)
    useEffect(() => {
        if (initialPageApplied || !study?.id || !loggedInUser) return;

        const effectiveTab = tab?.toLowerCase() === 'notes' ? 'sessions'
            : tab || (loggedInUser?.id == study?.leaderID ? 'sessions' : null);
        if (!effectiveTab) {
            setInitialPageApplied(true);
            return;
        }

        const mobileIndex = mobileRoutes.findIndex(r => r.key === effectiveTab || r.title === effectiveTab);
        const activityIndex = activityRoutes.findIndex(r => r.key === effectiveTab || r.title === effectiveTab);

        setTimeout(() => {
            if (mobileIndex !== -1) setMobileTab(mobileIndex);
            if (activityIndex !== -1) setActivityTab(activityIndex);
            setInitialPageApplied(true);
        }, 100);
    }, [tab, initialPageApplied, study?.id, study?.leaderID, loggedInUser, mobileRoutes, activityRoutes]);

    // ============ TAB SCENES ============
    const renderTabBar = useCallback((props) => <ReachTabBar {...props} />, []);

    const renderActivityScene = useCallback(({ route }) => {
        switch (route.key) {
            case 'sessions':
                return <SessionsTab smallGroupID={bibleStudyID} scope={canManage ? 'all' : 'own'} />;
            case 'students':
                return <StudentsTab smallGroupID={bibleStudyID} canReadNotes={canAct} />;
            default:
                return null;
        }
    }, [bibleStudyID, canManage, canAct]);

    const renderMobileScene = useCallback(({ route }) => {
        if (route.key === 'info') return <BibleStudyInfoPanel smallGroupID={bibleStudyID} canAct={canAct} />;
        return renderActivityScene({ route });
    }, [bibleStudyID, renderActivityScene, canAct]);

    // ============ LOADING STATE ============
    const renderLoadingState = () => (
        <Animated.View entering={FadeInDown.duration(300)} style={styles.loadingContainer}>
            <View style={styles.loadingHeaderRow}>
                <ContentLoader containerHeight={52} containerWidth={52} borderRadius={10} spacingVertical={0} />
                <View style={styles.loadingHeaderInfo}>
                    <ContentLoader containerHeight={22} containerWidth="70%" staggerPosition={1} spacingVertical={4} borderRadius={6} />
                    <ContentLoader containerHeight={14} containerWidth="40%" staggerPosition={1} spacingVertical={4} borderRadius={6} />
                </View>
            </View>
            <View style={styles.loadingStatsRow}>
                <ContentLoader containerHeight={40} borderRadius={8} staggerPosition={2} spacingVertical={0} style={styles.loadingStatItem} />
                <ContentLoader containerHeight={40} borderRadius={8} staggerPosition={2} spacingVertical={0} style={styles.loadingStatItem} />
                <ContentLoader containerHeight={40} borderRadius={8} staggerPosition={2} spacingVertical={0} style={styles.loadingStatItem} />
            </View>
            <ContentLoader containerHeight={180} borderRadius={12} staggerPosition={4} />
        </Animated.View>
    );

    // ============ MAIN RENDER ============
    if (!study?.id && fetchingStudy) return renderLoadingState();
    if (!study?.id && !fetchingStudy) return <EmptyListPlaceholder type="404" message="Bible Study not found" />;

    if (isWideScreen) {
        return (
            <View style={styles.container}>
                <View style={styles.twoColumn}>
                    <BibleStudyInfoPanel smallGroupID={bibleStudyID} canAct={canAct} />
                    <View style={styles.rightPanel}>
                        <TabView
                            navigationState={{ index: activityTab, routes: activityRoutes }}
                            renderScene={renderActivityScene}
                            onIndexChange={setActivityTab}
                            renderTabBar={renderTabBar}
                            swipeEnabled={isNative}
                            lazy
                        />
                    </View>
                </View>
            </View>
        );
    }

    return (
        <View style={styles.container}>
            <TabView
                navigationState={{ index: mobileTab, routes: mobileRoutes }}
                renderScene={renderMobileScene}
                onIndexChange={setMobileTab}
                renderTabBar={renderTabBar}
                swipeEnabled={isNative}
                lazy
            />
        </View>
    );
}


const styles = StyleSheet.create((theme, rt) => ({
    container: { flex: 1, backgroundColor: 'transparent' },

    // Loading State
    loadingContainer: { flex: 1, padding: 20, maxWidth: 500, width: rt.isLandscape ? '50%' : '100%' },
    loadingHeaderRow: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 20 },
    loadingHeaderInfo: { flex: 1, marginLeft: 16, justifyContent: 'center' },
    loadingStatsRow: { flexDirection: 'row', gap: 10, marginBottom: 20 },
    loadingStatItem: { flex: 1 },

    // Two Column Layout
    twoColumn: { flex: 1, flexDirection: 'row' },
    leftPanel: { flex: 1, maxWidth: 420 },
    leftPanelContent: { padding: 16, paddingBottom: 100 },
    rightPanel: { flex: 2 },

    // Header Card — CutCard owns surface/radius; the shadow stays here so it
    // converts to a clip-path drop-shadow on web.
    headerCard: {
        padding: 20,
        marginBottom: 16,
        boxShadow: theme.shadows.card
    },

    // Type Badge
    typeBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: theme.colors.groupsContainer,
        paddingHorizontal: 10,
        paddingVertical: 6,
        borderRadius: 6,
        marginBottom: 16,
        alignSelf: 'flex-start',
        gap: 6
    },
    typeBadgeText: { color: theme.colors.groups, fontSize: 12, fontFamily: theme.fonts.bold },

    // Title Row
    titleRow: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 16 },
    iconBadge: {
        width: 52,
        height: 52,
        backgroundColor: theme.colors.groupsContainer,
        borderRadius: 10,
        alignItems: 'center',
        justifyContent: 'center'
    },
    titleInfo: { flex: 1, marginLeft: 14 },
    groupTitle: { fontSize: 18, fontFamily: theme.fonts.heading, color: theme.colors.text },
    leaderSubtitle: { fontSize: 13, color: theme.colors.groups, marginTop: 2 },

    // Stats Row
    statsRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 14,
        borderTopWidth: 1,
        borderBottomWidth: 1,
        borderColor: theme.colors.border,
        marginBottom: 16
    },
    statItem: { flex: 1, alignItems: 'center' },
    statDivider: { width: 1, height: 28, backgroundColor: theme.colors.border },
    statLabel: { fontSize: 10, color: theme.colors.textSecondary, textTransform: 'uppercase', marginBottom: 2 },
    statValue: { fontSize: 13, fontFamily: theme.fonts.bold, color: theme.colors.text },

    // Status Pill
    statusPill: (bgColor, textColor) => ({
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: theme.colors[bgColor],
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 6,
        gap: 4,
        borderWidth: 1,
        borderColor: theme.colors[textColor]
    }),
    statusPillText: (textColor) => ({
        fontSize: 11,
        fontFamily: theme.fonts.bold,
        color: theme.colors[textColor],
        textTransform: 'capitalize'
    }),

    // Leader Row
    leaderRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        paddingBottom: 16,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.border,
        marginBottom: 16
    },
    leaderInfo: { flex: 1 },
    leaderLabel: { fontSize: 10, color: theme.colors.textSecondary, textTransform: 'uppercase' },
    leaderName: { fontSize: 14, fontFamily: theme.fonts.bold, color: theme.colors.text },

    // Action Buttons
    actionRow: { flexDirection: 'row', gap: 8, flexWrap: 'wrap' },

    // Info Section
    infoSection: {
        padding: 16,
        marginBottom: 16,
        boxShadow: theme.shadows.card
    },
    sectionLabel: {
        fontSize: 12,
        fontFamily: theme.fonts.bold,
        color: theme.colors.textSecondary,
        textTransform: 'uppercase',
        marginBottom: 12
    },
    infoRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        paddingVertical: 14,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.border
    },
    infoRowLeft: { flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1, maxWidth: 140 },
    infoRowLabel: { fontSize: 13, color: theme.colors.textSecondary },
    infoRowRight: { flex: 2, alignItems: 'flex-end' },
    infoRowValue: { fontSize: 14, color: theme.colors.text, textAlign: 'right' },
    linkText: { fontSize: 14, color: theme.colors.groups, textAlign: 'right' },
    emptyText: { fontSize: 14, color: theme.colors.textSecondary, fontStyle: 'italic', textAlign: 'right' },

    // Students Preview
    studentsPreview: {
        padding: 16,
        boxShadow: theme.shadows.card
    },
    studentsAvatarRow: { flexDirection: 'row', gap: 8, flexWrap: 'wrap' },
    moreBadge: {
        width: 36,
        height: 36,
        borderRadius: 18,
        backgroundColor: theme.colors.border,
        alignItems: 'center',
        justifyContent: 'center'
    },
    moreBadgeText: { fontSize: 11, color: theme.colors.textSecondary, fontFamily: theme.fonts.bold },

    // Tab Content
    tabContent: { flex: 1, paddingVertical: 16 },
    listPadding: { padding: 16, paddingBottom: 100 },
}));

export default BibleStudyDetailsScreen;
